import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/store";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async (n = 0) => {
      if (cancelled) return;
      if (n >= 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setData(data);
        setAttempt(n + 1);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success">
      {status === "checking" && (
        <>
          <Clock strokeWidth={1} className="h-16 w-16 mx-auto text-navy" />
          <h1 className="font-serif text-4xl mt-6">Confirmando tu pago…</h1>
          <p className="text-muted-foreground mt-3">Un momento. Intento {attempt}/8.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 strokeWidth={1} className="h-16 w-16 mx-auto text-navy" />
          <h1 className="font-serif text-5xl mt-6" data-testid="success-title">Gracias por tu compra</h1>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            Tu pedido <span className="font-mono-caps">{data?.order_id}</span> ha sido confirmado. Recibirás un email con los detalles y el número de seguimiento en breve.
          </p>
          <div className="mt-10 flex gap-3 justify-center">
            <Link to="/shop" className="border border-navy px-6 py-3 font-mono-caps hover:bg-navy hover:text-cream transition-colors">Seguir comprando</Link>
            <Link to="/" className="bg-navy text-cream px-6 py-3 font-mono-caps hover:bg-cream hover:text-navy transition-colors">Volver a inicio</Link>
          </div>
        </>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <>
          <XCircle strokeWidth={1} className="h-16 w-16 mx-auto text-destructive" />
          <h1 className="font-serif text-4xl mt-6">Pago no completado</h1>
          <p className="text-muted-foreground mt-3">Si crees que es un error, contacta con atención al cliente. Tu carrito sigue guardado.</p>
          <Link to="/checkout" className="inline-block mt-8 bg-navy text-cream px-6 py-3 font-mono-caps hover:bg-cream hover:text-navy transition-colors">Reintentar</Link>
        </>
      )}
    </div>
  );
}
